import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Select,
    MenuItem,
    Avatar,
    Paper,
    Button
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getMediaUrl } from '../../utils/urlHelper';
import { GET_All_EMPLOYEE_DETAILS } from '../../api/index';
import { SELECTED_MENU_PROPS } from '../../components/StyledComponents/StyledComponent';

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Parse a date string (YYYY-MM-DD or DD-MM-YYYY / DD-MM-YY) into {year, month, day}.
const parseDate = (str) => {
    if (!str) return null;
    const parts = String(str).split(/[-/]/);
    if (parts.length !== 3) return null;
    let year, month, day;
    if (parts[0].length === 4) {
        // YYYY-MM-DD
        year = parseInt(parts[0], 10);
        month = parseInt(parts[1], 10);
        day = parseInt(parts[2], 10);
    } else {
        // DD-MM-YYYY (common Indian format)
        day = parseInt(parts[0], 10);
        month = parseInt(parts[1], 10);
        year = parseInt(parts[2], 10);
    }
    if ([year, month, day].some(isNaN)) return null;
    // Normalise an obviously swapped month/day
    if (month > 12 && day <= 12) { [month, day] = [day, month]; }
    if (month < 1 || month > 12) return null;
    return { year, month, day };
};

const WorkAnniversaryWidget = () => {
    const [filter, setFilter] = useState('Today');
    const [page, setPage] = useState(0);
    const itemsPerPage = 3;

    const { data: emp, isLoading, isError } = useQuery({
        queryKey: ['employeeListDetailedForAnniversaries'],
        queryFn: GET_All_EMPLOYEE_DETAILS,
        staleTime: 1000 * 60 * 5,
    });

    const today = new Date();
    const currentMonth = today.getMonth() + 1; // 1-12
    const currentDay = today.getDate(); // 1-31

    const anniversaries = useMemo(() => {
        let todayList = [];
        let upcomingList = [];

        if (!Array.isArray(emp)) return { today: [], upcoming: [] };

        emp.forEach((employee) => {
            const doj = parseDate(employee.date_of_joining || employee.doj || employee.DOJ);
            if (!doj) return;

            // Completed years as of this year's anniversary date.
            let years = today.getFullYear() - doj.year;

            const isToday = (doj.month === currentMonth && doj.day === currentDay);

            let isUpcoming = false;
            let yearsAtEvent = years;
            if (!isToday) {
                const annivDate = new Date(today.getFullYear(), doj.month - 1, doj.day);
                let diffInDays = Math.ceil((annivDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
                if (diffInDays < 0) {
                    // Already passed this year — next occurrence is next year.
                    yearsAtEvent = today.getFullYear() + 1 - doj.year;
                    const nextYear = new Date(today.getFullYear() + 1, doj.month - 1, doj.day);
                    diffInDays = Math.ceil((nextYear.getTime() - today.getTime()) / (1000 * 3600 * 24));
                }
                if (diffInDays > 0 && diffInDays <= 182) { // ~6 months
                    isUpcoming = true;
                }
            }

            // Only celebrate completed years (>= 1); skip the joining day itself.
            const obj = {
                ...employee,
                displayDate: `${monthNames[doj.month - 1]} ${doj.day}`,
                sortVal: doj.month * 100 + doj.day,
            };

            if (isToday && years >= 1) {
                todayList.push({ ...obj, years });
            }
            if (isUpcoming && yearsAtEvent >= 1) {
                upcomingList.push({ ...obj, years: yearsAtEvent });
            }
        });

        upcomingList.sort((a, b) => {
            const currentVal = currentMonth * 100 + currentDay;
            let valA = a.sortVal;
            let valB = b.sortVal;
            if (valA < currentVal) valA += 1200;
            if (valB < currentVal) valB += 1200;
            return valA - valB;
        });

        return { today: todayList, upcoming: upcomingList };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emp, currentMonth, currentDay]);

    const displayList = filter === 'Today' ? anniversaries.today : anniversaries.upcoming;
    const totalPages = Math.ceil(displayList.length / itemsPerPage);
    const paginatedList = displayList.slice(page * itemsPerPage, (page + 1) * itemsPerPage);

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 2,
                background: '#fff',
                boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                border: '1px solid #f3f4f6',
                height: 'fit-content',
                width: '100%',
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight={800} color="#111827" sx={{ fontSize: '1rem' }}>
                    Work Anniversary
                </Typography>
            </Box>

            <Select
                value={filter}
                onChange={(e) => { setFilter(e.target.value); setPage(0); }}
                size="small"
                fullWidth
                MenuProps={SELECTED_MENU_PROPS}
                sx={{
                    mb: 3,
                    borderRadius: 3,
                    backgroundColor: '#f9fafb',
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    fontWeight: 500,
                    color: '#4b5563',
                }}
            >
                <MenuItem value="Today">Today</MenuItem>
                <MenuItem value="Upcoming">Upcoming</MenuItem>
            </Select>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: '200px' }}>
                {isLoading ? (
                    <Box sx={{ py: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" fontSize="0.8rem">Loading...</Typography>
                    </Box>
                ) : isError ? (
                    <Box sx={{ py: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="error" fontSize="0.8rem">Error</Typography>
                    </Box>
                ) : paginatedList.length > 0 ? (
                    paginatedList.map((person, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderBottom: idx !== paginatedList.length - 1 ? '1px solid #f3f4f6' : 'none',
                                pb: idx !== paginatedList.length - 1 ? 1.5 : 0,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar
                                    src={getMediaUrl(person.photo)}
                                    sx={{ width: 36, height: 36, bgcolor: '#f4f4f5', color: '#6366f1', fontWeight: 600, fontSize: '0.9rem' }}
                                >
                                    {person.name?.charAt(0)}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle2" fontWeight={700} color="#111827" sx={{ fontSize: '0.85rem' }}>
                                        {person.name || 'Employee'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                        {person.displayDate} - {person.years} {person.years === 1 ? 'Year' : 'Years'}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ fontSize: '1rem', pb: 1 }}>
                                🎊
                            </Box>
                        </Box>
                    ))
                ) : (
                    <Box sx={{ py: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                            No work anniversaries {filter.toLowerCase()}.
                        </Typography>
                    </Box>
                )}
            </Box>

            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                    <Button
                        disabled={page === 0}
                        onClick={() => setPage(p => p - 1)}
                        size="small"
                        sx={{ minWidth: 30, height: 24, p: 0, textTransform: 'none', fontSize: '0.7rem' }}
                    >
                        Prev
                    </Button>
                    <Typography variant="caption" sx={{ alignSelf: 'center', color: 'text.secondary', fontSize: '0.7rem' }}>
                        {page + 1} / {totalPages}
                    </Typography>
                    <Button
                        disabled={page === totalPages - 1}
                        onClick={() => setPage(p => p + 1)}
                        size="small"
                        sx={{ minWidth: 30, height: 24, p: 0, textTransform: 'none', fontSize: '0.7rem' }}
                    >
                        Next
                    </Button>
                </Box>
            )}
        </Paper>
    );
};

export default WorkAnniversaryWidget;
